import { createContext, useContext } from 'react'

/**
 * The hub's language, once a person is signed in (SHL-25, the UI half).
 *
 * ── WHERE THE LANGUAGE COMES FROM ───────────────────────────────────────────
 *
 * `me.locale`, which is `person.locale`. Migration 103 sets it to es-CL for Peru
 * and Chile by legal entity, and the person can change it from the identity
 * menu. App.tsx puts it in `HubLangContext` once, at the top of the signed-in
 * tree, and every component reads it with `useHubLang()`. It is NOT a prop
 * threaded through routes: AppShell's `locale` prop reached only two of nine
 * routes for a month (SHL-25 found it), and a context cannot be forgotten by
 * the tenth.
 *
 * The sign-in screen does NOT use this. Before sign-in there is no person, so
 * it follows the browser (lib/signInCopy.ts, B13). The two are deliberately
 * not linked.
 *
 * ── TWO LANGUAGES, NOT THREE ────────────────────────────────────────────────
 *
 * fr-FR is NOT OFFERED at all since 113 (David, 25 Sep): nobody can be set to
 * French until there are French pages, because a French choice on English
 * screens is a control that does not do what it says. Content can still carry
 * a French variant. Adding French is a third column in lib/hubCopy.ts, one line
 * here, and fr-FR back in lib/locale.ts and app.supported_locales() — one
 * release; the type makes a missing string a compile error.
 *
 * ── THE DEFAULT IS ENGLISH, AND THAT IS WHAT KEEPS THE SUITE HONEST ─────────
 *
 * A component rendered with no provider — which is everything the web suite
 * mounts — gets English. So every existing assertion keeps measuring what it
 * measured, and the Spanish is asserted by wrapping in a provider on purpose.
 */
export type HubLang = 'en' | 'es'

export function hubLangFor(locale: string | null | undefined): HubLang {
  const tag = (locale ?? '').toLowerCase()
  return tag === 'es' || tag.startsWith('es-') ? 'es' : 'en'
}

/** For `<html lang>` and `Intl`. */
export function htmlLangFor(lang: HubLang): string {
  return lang === 'es' ? 'es' : 'en'
}

export const HubLangContext = createContext<HubLang>('en')

export function useHubLang(): HubLang {
  return useContext(HubLangContext)
}

/**
 * The locale to hand `toLocaleDateString` and friends. English passes
 * `undefined` — the browser's own — which is what every date on the hub did
 * before SHL-25, so nothing an English reader sees moves. Spanish passes 'es',
 * because a Spanish screen with "Sep 24, 2026" in it (an English browser in a
 * Lima office) reads as half-translated.
 */
export function dateLocaleFor(lang: HubLang): string | undefined {
  return lang === 'es' ? 'es' : undefined
}
